import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { Badge, Button, Card, CardBody, CardHeader, Input } from '../../design-system';
import { tokens } from '../../design-system/tokens';
import { channelLabel, CHANNEL_SIDEBAR_ORDER } from './conversationChannelMeta';
import { useTenantPermissions } from '../../auth/TenantPermissionsContext';
import { useAgentSelector } from '../../auth/AgentSelectorContext';
import { showGrowl } from '../../ui/growlState';

type BlacklistEntry = {
  id: string;
  channel: string;
  channelIntegrationId: string | null;
  externalThreadId: string | null;
  label: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  integrationDisplayName?: string | null;
};

type Integration = {
  id: string;
  provider: string;
  displayName: string | null;
};

const CONTACT_CHANNELS = CHANNEL_SIDEBAR_ORDER.filter((c) =>
  ['demo', 'whatsapp_js', 'whatsapp_meta', 'messenger', 'instagram', 'web', 'web_widget', 'telegram'].includes(c)
);

function providersForChannel(channel: string): string[] {
  if (channel === 'messenger') return ['facebook'];
  if (channel === 'web_widget') return ['web_widget'];
  return [channel];
}

function IconArrowLeft() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const pageStyle: CSSProperties = {
  maxWidth: 1180,
  margin: '0 auto',
  padding: `${tokens.spacing[7]} ${tokens.spacing[6]} ${tokens.spacing[10]}`,
  color: tokens.semantic.textPrimary,
};

const backLinkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.spacing[2],
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.colors.primary[600],
  textDecoration: 'none',
  marginBottom: tokens.spacing[4],
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize['2xl'],
  fontWeight: tokens.typography.fontWeight.bold,
  color: tokens.colors.neutral[900],
  letterSpacing: '-0.03em',
};

const subtitleStyle: CSSProperties = {
  margin: `${tokens.spacing[2]} 0 0`,
  fontSize: tokens.typography.fontSize.sm,
  color: tokens.colors.neutral[500],
  maxWidth: 720,
  lineHeight: tokens.typography.lineHeight.relaxed,
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: tokens.typography.fontSize.xs,
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.colors.neutral[500],
  marginBottom: tokens.spacing[1],
};

const fieldControlStyle: CSSProperties = {
  width: '100%',
  padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
  fontSize: tokens.typography.fontSize.sm,
  borderRadius: tokens.borderRadius.md,
  border: `1px solid ${tokens.colors.neutral[300]}`,
  backgroundColor: tokens.colors.neutral[50],
  color: tokens.colors.neutral[900],
  outline: 'none',
  fontFamily: tokens.typography.fontFamily.sans,
  boxSizing: 'border-box',
};

const thStyle: CSSProperties = {
  padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
  fontSize: tokens.typography.fontSize.xs,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.colors.neutral[500],
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  textAlign: 'left',
  background: tokens.colors.neutral[50],
  borderBottom: `1px solid ${tokens.semantic.border}`,
};

const tdStyle: CSSProperties = {
  padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
  fontSize: tokens.typography.fontSize.sm,
  color: tokens.colors.neutral[800],
  verticalAlign: 'middle',
  borderBottom: `1px solid ${tokens.colors.neutral[100]}`,
};

export function ContactBlacklistPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY) ?? '';
  const { can } = useTenantPermissions();
  const { selectedAgentId } = useAgentSelector();
  const canWrite = can('web.conversations.write');

  const [items, setItems] = useState<BlacklistEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [q, setQ] = useState('');

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [formChannel, setFormChannel] = useState('whatsapp_js');
  const [formIntegrationId, setFormIntegrationId] = useState('');
  const [formExternalId, setFormExternalId] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams({ pageSize: '50' });
      if (channelFilter) params.set('channel', channelFilter);
      if (q.trim()) params.set('q', q.trim());
      if (selectedAgentId) params.set('agentId', selectedAgentId);
      const data = await apiGet<{ items: BlacklistEntry[]; total: number }>(
        `/api/console/contact-blacklist?${params}`,
        token
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setErr((e as Error).message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, channelFilter, q, selectedAgentId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    const qs = selectedAgentId ? `?agentId=${encodeURIComponent(selectedAgentId)}` : '';
    void apiGet<Integration[]>(`/api/console/integrations${qs}`, token)
      .then(setIntegrations)
      .catch(() => setIntegrations([]));
  }, [token, selectedAgentId]);

  const filteredIntegrations = useMemo(() => {
    const providers = providersForChannel(formChannel);
    return integrations.filter((i) => providers.includes(i.provider));
  }, [integrations, formChannel]);

  const submitManual = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setErr('');
    try {
      await apiPost(
        '/api/console/contact-blacklist',
        {
          channel: formChannel,
          channelIntegrationId: formIntegrationId || null,
          externalThreadId: formExternalId.trim() || null,
          label: formLabel.trim() || null,
          notes: formNotes.trim() || null,
        },
        token,
        { successMessage: 'Entrada agregada a la lista negra' }
      );
      setFormExternalId('');
      setFormLabel('');
      setFormNotes('');
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (entry: BlacklistEntry) => {
    if (!canWrite) return;
    try {
      await apiPatch(
        `/api/console/contact-blacklist/${entry.id}`,
        { isActive: !entry.isActive },
        token,
        { successMessage: entry.isActive ? 'Entrada desactivada' : 'Entrada reactivada' }
      );
      void load();
    } catch (ex) {
      showGrowl((ex as Error).message, 'error');
    }
  };

  return (
    <div style={pageStyle}>
      <Link to="/app/conversations" style={backLinkStyle}>
        <IconArrowLeft />
        Volver a conversaciones
      </Link>

      <header style={{ marginBottom: tokens.spacing[6] }}>
        <h1 style={titleStyle}>Lista negra de contactos</h1>
        <p style={subtitleStyle}>
          Bloqueá orígenes de conversación (canal, integración o contacto) para que no puedan consultar
          disponibilidad ni agendar por el plugin de calendario. Las reservas sin{' '}
          <code
            style={{
              fontSize: tokens.typography.fontSize.xs,
              padding: '2px 6px',
              borderRadius: tokens.borderRadius.sm,
              background: tokens.colors.neutral[100],
              color: tokens.colors.neutral[700],
            }}
          >
            conversationId
          </code>{' '}
          no quedan cubiertos hasta vincularlos al hilo.
        </p>
      </header>

      {err ? (
        <div
          role="alert"
          style={{
            padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
            marginBottom: tokens.spacing[4],
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            border: `1px solid ${tokens.colors.error.main}`,
            color: tokens.colors.error.dark,
            fontSize: tokens.typography.fontSize.sm,
          }}
        >
          {err}
        </div>
      ) : null}

      {canWrite ? (
        <Card variant="outlined" padding="none" style={{ marginBottom: tokens.spacing[6] }}>
          <CardHeader
            title="Alta manual"
            subtitle="Agregá un bloqueo sin salir de una conversación."
            style={{
              padding: `${tokens.spacing[4]} ${tokens.spacing[5]}`,
              marginBottom: 0,
              borderBottom: `1px solid ${tokens.semantic.border}`,
            }}
          />
          <CardBody style={{ padding: tokens.spacing[5] }}>
            <form onSubmit={(e) => void submitManual(e)}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: tokens.spacing[4],
                }}
              >
                <label>
                  <span style={fieldLabelStyle}>Canal</span>
                  <select
                    value={formChannel}
                    onChange={(e) => {
                      setFormChannel(e.target.value);
                      setFormIntegrationId('');
                    }}
                    style={fieldControlStyle}
                  >
                    {CONTACT_CHANNELS.map((c) => (
                      <option key={c} value={c}>
                        {channelLabel(c)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span style={fieldLabelStyle}>Integración (opcional)</span>
                  <select
                    value={formIntegrationId}
                    onChange={(e) => setFormIntegrationId(e.target.value)}
                    style={fieldControlStyle}
                  >
                    <option value="">Cualquiera del canal</option>
                    {filteredIntegrations.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.displayName ?? i.provider} ({i.id.slice(0, 8)}…)
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span style={fieldLabelStyle}>Identificador externo (opcional)</span>
                  <input
                    value={formExternalId}
                    onChange={(e) => setFormExternalId(e.target.value)}
                    placeholder="Teléfono, PSID, IGSID…"
                    style={fieldControlStyle}
                  />
                </label>
                <label>
                  <span style={fieldLabelStyle}>Motivo (consola)</span>
                  <input
                    value={formLabel}
                    onChange={(e) => setFormLabel(e.target.value)}
                    placeholder="Ej. spam o abuso"
                    style={fieldControlStyle}
                  />
                </label>
              </div>
              <label style={{ display: 'block', marginTop: tokens.spacing[4] }}>
                <span style={fieldLabelStyle}>Notas</span>
                <textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={2}
                  style={{ ...fieldControlStyle, resize: 'vertical', minHeight: 72 }}
                />
              </label>
              <div style={{ marginTop: tokens.spacing[5], display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" variant="cta" loading={saving} disabled={saving}>
                  Agregar a la lista
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      ) : null}

      <Card variant="outlined" padding="md" style={{ marginBottom: tokens.spacing[5] }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.spacing[3], alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 160px', minWidth: 140 }}>
            <span style={fieldLabelStyle}>Canal</span>
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              style={fieldControlStyle}
            >
              <option value="">Todos los canales</option>
              {CONTACT_CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {channelLabel(c)}
                </option>
              ))}
            </select>
          </label>
          <div style={{ flex: '2 1 220px', minWidth: 200 }}>
            <Input
              label="Buscar"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Motivo o identificador…"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter') void load();
              }}
            />
          </div>
          <Button type="button" variant="secondary" onClick={() => void load()}>
            Aplicar filtros
          </Button>
        </div>
        <p
          style={{
            margin: `${tokens.spacing[4]} 0 0`,
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.neutral[500],
          }}
        >
          {loading ? 'Cargando…' : `${total} entrada${total === 1 ? '' : 's'}`}
        </p>
      </Card>

      <Card variant="outlined" padding="none" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div
            style={{
              padding: tokens.spacing[8],
              textAlign: 'center',
              color: tokens.colors.neutral[500],
              fontSize: tokens.typography.fontSize.sm,
            }}
          >
            Cargando entradas…
          </div>
        ) : items.length === 0 ? (
          <div
            style={{
              padding: tokens.spacing[10],
              textAlign: 'center',
              color: tokens.colors.neutral[500],
              fontSize: tokens.typography.fontSize.sm,
            }}
          >
            No hay entradas con estos filtros.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Canal</th>
                  <th style={thStyle}>Integración</th>
                  <th style={thStyle}>Identificador</th>
                  <th style={thStyle}>Motivo</th>
                  <th style={thStyle}>Fecha</th>
                  <th style={thStyle}>Estado</th>
                  {canWrite ? <th style={{ ...thStyle, width: 1 }} /> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td style={tdStyle}>{channelLabel(row.channel)}</td>
                    <td style={tdStyle}>
                      {row.integrationDisplayName ?? (row.channelIntegrationId ? '—' : 'Todas')}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        fontFamily: tokens.typography.fontFamily.mono,
                        fontSize: tokens.typography.fontSize.xs,
                      }}
                    >
                      {row.externalThreadId ?? '—'}
                    </td>
                    <td style={tdStyle}>{row.label ?? '—'}</td>
                    <td style={{ ...tdStyle, color: tokens.colors.neutral[500], whiteSpace: 'nowrap' }}>
                      {new Date(row.createdAt).toLocaleString('es-UY')}
                    </td>
                    <td style={tdStyle}>
                      <Badge variant={row.isActive ? 'success' : 'neutral'} size="sm">
                        {row.isActive ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    {canWrite ? (
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <Button
                          type="button"
                          variant={row.isActive ? 'outline' : 'secondary'}
                          size="sm"
                          onClick={() => void toggleActive(row)}
                        >
                          {row.isActive ? 'Desactivar' : 'Reactivar'}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
